import React, {FC, useMemo} from 'react';

import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {ButtonVariantsEnum} from '../../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {
  SettingsInboxesResourcePagesEnum,
  SettingsInboxesTabPagesEnum,
} from '../../../../../models/url/models/settingsInboxesUrlModel';
import {SettingsResourcePagesEnum, SettingsRootPagesEnum} from '../../../../../models/url/models/types';
import {buildSettingsPath} from '../../../../../models/url/settingsUrlHelpers';
import {IntegrationIconsEnum} from '../../../../common/integrationIcon';
import {
  CellsEmptySetupActionsProps,
  StyledCellsEmptySetupActionsContentDiv,
  StyledCellsEmptySetupActionsSubTitleDiv,
  StyledCellsEmptySetupActionsTitleDiv,
  StyledCellsEmptySetupActionsWrapperDiv,
} from './cellsEmptyCommon';
import {CellsEmptySetupActionsButton} from './cellsEmptySetupActionsButton';
import {CellsEmptySetupActionsDismissButton} from './cellsEmptySetupActionsDismissButton';
import {CellsEmptySetupActionsLink} from './cellsEmptySetupActionsLink';

/*
 * Component.
 */

export const CellsEmptyTeamSetupActions: FC<CellsEmptySetupActionsProps> = (props) => {
  // If we've selected a demo inbox, the copy should reflect that.
  const renderTitleCopy = () =>
    props.selectedTeamDemoInbox ? (
      <Trans id="cells-empty-cta-team-title-demo">This is an empty sample inbox.</Trans>
    ) : (
      <Trans id="cells-empty-cta-team-title">Add a shared inbox</Trans>
    );

  const renderSubtitleCopy = () =>
    props.selectedTeamDemoInbox ? (
      <Trans id="cells-empty-cta-team-subtitle-demo">
        Feel free to connect your team&apos;s accounts to start handling your own messages in Front.
      </Trans>
    ) : (
      <Trans id="cells-empty-cta-team-subtitle">
        Everyone in your team will have access to the messages in here.
      </Trans>
    );

  const newInboxCreationPath = useMemo(() => {
    if (!props.namespace) {
      return '';
    }

    if (!props.selectedTeamDemoInbox) {
      return buildSettingsPath({
        namespace: props.namespace,
        rootPage: SettingsRootPagesEnum.INBOXES,
        resourcePage: SettingsInboxesResourcePagesEnum.NEW,
        tabPage: SettingsInboxesTabPagesEnum.INBOXES,
      });
    }

    return buildSettingsPath({
      namespace: props.namespace,
      rootPage: SettingsRootPagesEnum.CHANNELS,
      inboxId: props.selectedTeamDemoInbox.id,
      resourcePage: SettingsResourcePagesEnum.NEW,
    });
  }, [props.namespace, props.selectedTeamDemoInbox]);

  return (
    <StyledCellsEmptySetupActionsWrapperDiv>
      <StyledCellsEmptySetupActionsContentDiv>
        <StyledCellsEmptySetupActionsTitleDiv>{renderTitleCopy()}</StyledCellsEmptySetupActionsTitleDiv>
        <StyledCellsEmptySetupActionsSubTitleDiv>
          {renderSubtitleCopy()}
        </StyledCellsEmptySetupActionsSubTitleDiv>
        {props.namespace && (
          <>
            <CellsEmptySetupActionsButton
              buttonVariant={ButtonVariantsEnum.SECONDARY}
              interactionId="cells-empty-cta-team-googlegroupsbutton"
              title={<Trans id="cells-empty-cta-team-googlegroupsbutton">Google Groups</Trans>}
              integrationIcon={IntegrationIconsEnum.GOOGLE}
              onClick={props.navigate(newInboxCreationPath)}
            />
            <CellsEmptySetupActionsButton
              buttonVariant={ButtonVariantsEnum.SECONDARY}
              interactionId="cells-empty-cta-team-gmailbutton"
              title={<Trans id="cells-empty-cta-team-gmailbutton">Gmail Account</Trans>}
              integrationIcon={IntegrationIconsEnum.GMAIL}
              onClick={props.navigate(newInboxCreationPath)}
            />
            <CellsEmptySetupActionsButton
              buttonVariant={ButtonVariantsEnum.SECONDARY}
              interactionId="cells-empty-cta-team-office365button"
              title={<Trans id="cells-empty-cta-team-office365button">Office 365</Trans>}
              integrationIcon={IntegrationIconsEnum.OFFICE365}
              onClick={props.navigate(newInboxCreationPath)}
            />
            <CellsEmptySetupActionsButton
              buttonVariant={ButtonVariantsEnum.SECONDARY}
              interactionId="cells-empty-cta-team-twiliobutton"
              title={<Trans id="cells-empty-cta-team-twiliobutton">Twilio SMS</Trans>}
              integrationIcon={IntegrationIconsEnum.TWILIO}
              onClick={props.navigate(newInboxCreationPath)}
            />
            <CellsEmptySetupActionsButton
              buttonVariant={ButtonVariantsEnum.SECONDARY}
              interactionId="cells-empty-cta-team-facebookbutton"
              title={<Trans id="cells-empty-cta-team-facebookbutton">Facebook</Trans>}
              integrationIcon={IntegrationIconsEnum.MESSENGER}
              onClick={props.navigate(newInboxCreationPath)}
            />
            <CellsEmptySetupActionsLink
              interactionId="cells-empty-cta-team-morelink"
              title={<Trans id="cells-empty-cta-team-morelink">More options</Trans>}
              onClick={props.navigate(newInboxCreationPath)}
            />
          </>
        )}
      </StyledCellsEmptySetupActionsContentDiv>
      <CellsEmptySetupActionsDismissButton
        interactionId="cells-empty-cta-team-dismissbutton"
        onClick={props.onDismissClick}
      />
    </StyledCellsEmptySetupActionsWrapperDiv>
  );
};
